import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAllUsers, fetchPortalSignIns, fetchStaffDevices } from '../api';
import { deviceName } from '../../utils/deviceName';
import { DAY, ago, day, displayName, initialsOf, isStale } from './format';
import UserForm from './UserForm';
import DeleteUserModal from '../DeleteUserModal';
import './UserManagement.css';

// Admin Settings > User Management: the staff who use this app (with the devices they
// use it on), and who is signed in to the customer portal on the website. Seeing where
// someone is signed in never signs them out.

const initialsFor = (u) => u.initials || initialsOf(displayName(u));

function RoleBadges({ user }) {
  return (
    <span className="um-badges">
      {user.is_admin && <span className="um-badge um-badge-admin">Admin</span>}
      {user.is_sales_advanced
        ? <span className="um-badge um-badge-sales">Sales Advanced</span>
        : user.is_sales_base && <span className="um-badge um-badge-sales">Sales Base</span>}
    </span>
  );
}

function StaffDevices({ devices }) {
  if (devices.length === 0) {
    return (
      <div className="um-devices um-muted">
        No devices recorded yet. Devices show up here the next time they use the app.
      </div>
    );
  }
  return (
    <ul className="um-devices">
      {devices.map((d) => (
        <li key={d.id} className={isStale(d.last_seen_at) ? 'um-stale' : ''}>
          <div className="um-device-main">
            <span className="um-device-name" title={d.device}>{deviceName(d.device)}</span>
            {d.this_device && <span className="um-chip">This device</span>}
          </div>
          <div className="um-device-meta">
            <span>Last active {ago(d.last_seen_at)}</span>
            {d.ip && <span>from {d.ip}</span>}
            <span>{d.signed_in_at ? `signed in ${day(d.signed_in_at)}` : `first seen ${day(d.first_seen_at)}`}</span>
            {isStale(d.last_seen_at) && <span>not used in over a month</span>}
          </div>
        </li>
      ))}
    </ul>
  );
}

function StaffRow({ user, devices, isYou, expanded, onToggle, onEdit }) {
  const lastActive = devices[0]?.last_seen_at;
  const name = displayName(user);
  return (
    <li className={`um-row ${expanded ? 'um-row-open' : ''}`}>
      <div className="um-row-main">
        <span className={`um-avatar ${user.is_admin ? 'um-avatar-admin' : ''}`} aria-hidden="true">{initialsFor(user)}</span>
        <div className="um-who">
          <div className="um-name">
            {name}
            {isYou && <span className="um-you">You</span>}
            <RoleBadges user={user} />
          </div>
          <div className="um-sub">
            {name !== user.username && <span>{user.username}</span>}
            {user.email && <span>{user.email}</span>}
          </div>
        </div>
        <button
          type="button"
          className="um-activity"
          onClick={onToggle}
          aria-expanded={expanded}
          title={expanded ? 'Hide devices' : 'Show devices'}
        >
          <span className={`um-dot ${lastActive && !isStale(lastActive) ? 'um-dot-on' : ''}`} />
          <span className="um-activity-text">
            <span>{lastActive ? `Active ${ago(lastActive)}` : 'Not seen yet'}</span>
            {devices.length > 0 && (
              <span className="um-muted">{devices.length === 1 ? '1 device' : `${devices.length} devices`}</span>
            )}
          </span>
          <span className="um-caret" aria-hidden="true">{expanded ? '▴' : '▾'}</span>
        </button>
        <button type="button" className="um-btn" onClick={onEdit}>Edit</button>
      </div>
      {expanded && <StaffDevices devices={devices} />}
    </li>
  );
}

function StaffList({ currentUser, search }) {
  const [users, setUsers] = useState(null);
  const [devices, setDevices] = useState([]);
  const [devicesError, setDevicesError] = useState('');
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());
  const [editing, setEditing] = useState(null); // null, 'new' or a user
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    try {
      setUsers(await fetchAllUsers());
      setError('');
    } catch (err) {
      setError('Could not load users.');
    }
    try {
      setDevices(await fetchStaffDevices());
      setDevicesError('');
    } catch (err) {
      setDevicesError('Could not load devices.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const devicesByUser = useMemo(() => {
    const map = {};
    devices.forEach((d) => { (map[d.user_id] = map[d.user_id] || []).push(d); });
    return map;
  }, [devices]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (users || [])
      .filter((u) => !q || [displayName(u), u.username, u.email, u.initials].some((v) => (v || '').toLowerCase().includes(q)))
      .sort((a, b) => displayName(a).localeCompare(displayName(b), undefined, { sensitivity: 'base' }));
  }, [users, search]);

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const activeCount = (users || []).filter((u) => {
    const last = devicesByUser[u.id]?.[0]?.last_seen_at;
    return last && Date.now() - new Date(last).getTime() < 7 * DAY;
  }).length;

  return (
    <>
      <div className="um-toolbar">
        <div className="um-summary">
          {users ? `${users.length} users · ${activeCount} active this week` : 'Loading users…'}
        </div>
        <button type="button" className="um-btn um-btn-primary" onClick={() => setEditing('new')}>Add user</button>
      </div>
      {error && <div className="um-error">{error}</div>}
      {devicesError && <div className="um-error">{devicesError}</div>}
      {users && (
        <ul className="um-list">
          {shown.map((u) => (
            <StaffRow
              key={u.id}
              user={u}
              devices={devicesByUser[u.id] || []}
              isYou={u.id === currentUser.id}
              expanded={expanded.has(u.id)}
              onToggle={() => toggle(u.id)}
              onEdit={() => setEditing(u)}
            />
          ))}
          {shown.length === 0 && <li className="um-empty">No users match “{search}”.</li>}
        </ul>
      )}
      <p className="um-footnote">
        Staff stay signed in until they sign out on that device. Devices are listed from when they were first
        used after this list was added; signing out on one device doesn't sign out the others.
      </p>

      {editing && (
        <UserForm
          user={editing === 'new' ? null : editing}
          isYou={editing !== 'new' && editing.id === currentUser.id}
          onSaved={() => { setEditing(null); load(); }}
          onDelete={editing !== 'new' ? () => { setDeleting(editing); setEditing(null); } : null}
          onCancel={() => setEditing(null)}
        />
      )}
      {deleting && (
        <DeleteUserModal
          user={deleting}
          onUserDeleted={() => { setDeleting(null); load(); }}
          onCancel={() => setDeleting(null)}
        />
      )}
    </>
  );
}

function PortalSignIns({ search, onOpenPortal }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPortalSignIns()
      .then((result) => { setData(result); setError(''); })
      .catch(() => setError('Could not load customer portal sign-ins.'));
  }, []);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.people || []).filter((p) => !q || [p.name, p.email, ...p.businesses.map((b) => b.name)]
      .some((v) => (v || '').toLowerCase().includes(q)));
  }, [data, search]);

  if (error) return <div className="um-error">{error}</div>;
  if (!data) return <div className="um-muted um-loading">Loading customer portal sign-ins…</div>;

  const deviceCount = data.people.reduce((n, p) => n + p.sessions.length, 0);
  return (
    <>
      <div className="um-toolbar">
        <div className="um-summary">
          {data.people.length === 1 ? '1 person' : `${data.people.length} people`} signed in on{' '}
          {deviceCount === 1 ? '1 device' : `${deviceCount} devices`}
          {data.not_signed_in > 0 && ` · ${data.not_signed_in} with access not signed in`}
        </div>
        <button type="button" className="um-btn" onClick={() => onOpenPortal()}>Open Commercial Portal</button>
      </div>
      <ul className="um-list">
        {shown.map((p) => (
          <li key={p.id} className="um-row um-row-open">
            <div className="um-row-main">
              <span className="um-avatar um-avatar-portal" aria-hidden="true">
                {initialsOf(p.name)}
              </span>
              <div className="um-who">
                <div className="um-name">
                  {p.name}
                  {p.title && <span className="um-muted um-title">{p.title}</span>}
                </div>
                <div className="um-sub">
                  <span>{p.email}</span>
                  {p.businesses.map((b) => (
                    <button
                      key={b.account_id}
                      type="button"
                      className="um-business"
                      onClick={() => onOpenPortal(b.account_id)}
                      title="Open this business on the Commercial Portal page"
                    >
                      {b.name}{!b.enabled && ' (portal off)'}
                    </button>
                  ))}
                  {p.businesses.length === 0 && <span className="um-muted">No active business</span>}
                </div>
              </div>
            </div>
            <ul className="um-devices">
              {p.sessions.map((s) => (
                <li key={s.id}>
                  <div className="um-device-main">
                    <span className="um-device-name" title={s.device}>{deviceName(s.device)}</span>
                  </div>
                  <div className="um-device-meta">
                    <span>Last used {ago(s.last_used_at)}</span>
                    {s.last_ip && <span>from {s.last_ip}</span>}
                    <span>signed in {day(s.created_at)}</span>
                    <span>{s.remember ? `stays signed in until ${day(s.expires_at)} unless used` : 'this visit only'}</span>
                  </div>
                </li>
              ))}
            </ul>
          </li>
        ))}
        {data.people.length === 0 && <li className="um-empty">Nobody is signed in to the customer portal right now.</li>}
        {data.people.length > 0 && shown.length === 0 && <li className="um-empty">Nobody matches “{search}”.</li>}
      </ul>
      <p className="um-footnote">
        Customers sign in on the website with an emailed code. To sign someone out or change who has access,
        open their business on the Commercial Portal page.
      </p>
    </>
  );
}

function UserManagement({ currentUser, onClose }) {
  const navigate = useNavigate();
  const [view, setView] = useState('staff');
  const [search, setSearch] = useState('');

  const openPortal = (accountId) => {
    if (onClose) onClose(); // close the settings overlay
    navigate(accountId ? `/portal?account=${accountId}` : '/portal');
  };

  return (
    <div className="um">
      <div className="um-header">
        <div className="um-segmented" role="tablist" aria-label="Which accounts">
          <button
            type="button"
            role="tab"
            aria-selected={view === 'staff'}
            className={view === 'staff' ? 'active' : ''}
            onClick={() => setView('staff')}
          >
            Staff
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === 'portal'}
            className={view === 'portal' ? 'active' : ''}
            onClick={() => setView('portal')}
          >
            Customer portal
          </button>
        </div>
        <input
          type="search"
          className="um-search"
          placeholder={view === 'staff' ? 'Search name, username or email' : 'Search people or businesses'}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          aria-label="Search"
        />
      </div>

      {view === 'staff'
        ? <StaffList currentUser={currentUser} search={search} />
        : <PortalSignIns search={search} onOpenPortal={openPortal} />}
    </div>
  );
}

export default UserManagement;
